import { useState } from "react";
import { Mail, Phone, ShieldCheck, BadgeCheck, Clock, MapPin } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import LegalLayout, { LEGAL_CONTACT } from "@/components/LegalLayout";

export default function Contact() {
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget).entries());
    setLoading(true);
    try {
      await api.post("/enquiries", { name: data.name, email: data.email, phone: data.phone || "", business: "", city: "", btype: "contact", message: data.message });
      toast.success("Message sent! A human replies within a few hours.");
      e.currentTarget.reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <LegalLayout
      eyebrow="Say hello"
      title="CONTACT US."
      intro="Questions about an order, bulk gifting, or just want to argue about the best way to make chai? Talk to us."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <a href={`mailto:${LEGAL_CONTACT.email}`} data-testid="contact-email-card" className="flex items-center gap-4 rounded-3xl border border-sand bg-white p-6 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-forest/10">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest text-cream"><Mail className="h-5 w-5" /></span>
          <span>
            <span className="block text-xs font-bold uppercase tracking-wider text-ink/50">Email</span>
            <span className="block font-display text-base font-extrabold text-forest">{LEGAL_CONTACT.email}</span>
          </span>
        </a>
        <a href="tel:+917020724570" data-testid="contact-phone-card" className="flex items-center gap-4 rounded-3xl border border-sand bg-white p-6 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-forest/10">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest text-cream"><Phone className="h-5 w-5" /></span>
          <span>
            <span className="block text-xs font-bold uppercase tracking-wider text-ink/50">Call / WhatsApp</span>
            <span className="block font-display text-base font-extrabold text-forest">{LEGAL_CONTACT.phone}</span>
          </span>
        </a>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {[
          { icon: MapPin, t: "Registered office", d: LEGAL_CONTACT.address },
          { icon: Clock, t: "Response time", d: "Within a few hours, every day. A human replies — no bots." },
          { icon: BadgeCheck, t: "FSSAI licence", d: LEGAL_CONTACT.fssai },
          { icon: ShieldCheck, t: "GSTIN", d: LEGAL_CONTACT.gst },
        ].map((c) => (
          <div key={c.t} className="rounded-3xl border border-sand bg-white p-6">
            <c.icon className="h-5 w-5 text-leaf" />
            <p className="mt-3 font-display text-sm font-extrabold text-forest">{c.t}</p>
            <p className="mt-1 text-xs leading-relaxed text-ink/60">{c.d}</p>
          </div>
        ))}
      </div>

      <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-sand bg-white p-6 sm:p-8">
        <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-forest">Drop a message</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[
            { name: "name", label: "Name", ph: "Aarav Sharma", req: true },
            { name: "phone", label: "Phone (optional)", ph: "98765 43210", req: false },
            { name: "email", label: "Email", ph: "you@example.com", req: true },
          ].map((f) => (
            <label key={f.name}>
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink/60">{f.label}</span>
              <input data-testid={`contact-field-${f.name}`} name={f.name} required={f.req} placeholder={f.ph} className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-3 text-sm font-semibold focus:border-leaf focus:bg-white" />
            </label>
          ))}
          <label className="sm:col-span-2">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink/60">Message</span>
            <textarea data-testid="contact-field-message" name="message" required rows="4" placeholder="What's on your mind?" className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-3 text-sm font-semibold focus:border-leaf focus:bg-white" />
          </label>
        </div>
        <button data-testid="contact-submit" disabled={loading} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-forest py-4 text-sm font-extrabold uppercase tracking-wide text-cream transition-all hover:bg-leaf active:scale-[0.98] disabled:opacity-60">
          {loading ? "Sending..." : "Send message"}
        </button>
      </form>

      <p className="text-center font-hand text-2xl text-forest/60">goodness, naturally — from all of us at {LEGAL_CONTACT.brand}.</p>
    </LegalLayout>
  );
}
